import React, { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Card } from '../../../../../../shared/card/card'
import { ConfirmDialog } from '../../../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'
import { useRoleSysApiKey } from '../../../../../../role/hook/role-sys'
import { getOrgAccess, setOrgAccess } from '../../../api-keys/sys-api'
import { ACCESS_OFF_BANNER, reasonProblem } from '../../../../org/api-keys/key-words'
import { scrollToElement } from '../../../../../../shared/sso-workspace/section-nav'

// An organization's partner API access (Phase D D8; plan Task 16, ruling
// 9): the staff-only switch, on the organization's system page. Off, every
// key of the organization is refused from its next request (nothing is
// revoked: on again restores them) and no key can be made. Content keeps it
// in organizations.feature_partner_api, outside the organization form, so
// it is read and switched through its own route, with a reason
// (api_access.enabled / api_access.disabled in the audit trail).
//
// Shown with SYS_API_KEY read; switched with SYS_API_KEY update. Content
// refuses an impersonated session, reads included, and the card says so in
// the server's words.
//
// The same card sits inline on the organization's own API keys page for
// staff while the access is off (owner 2026-10-23); onChanged hears each
// switch, so that page can offer Create at once. Platform -> API keys links
// here with #api-access (an organization, or its "Access off"): the card is
// then scrolled into view once it has loaded.
export const API_ACCESS_ANCHOR = 'api-access'

export const ApiAccessToggle = ({ organizationID, onChanged }) => {
  const role = useRoleSysApiKey()
  if (!role.accessRead) return null
  return <AccessCard organizationID={organizationID} canUpdate={!!role.accessUpdate} onChanged={onChanged} />
}

const AccessCard = ({ organizationID, canUpdate, onChanged }) => {
  const ids = { label: useId(), reason: useId() }
  const [access, setAccess] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [next, setNext] = useState(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [saveError, saveFailure, setSaveError] = useErrorLine()
  const { hash } = useLocation()
  const section = useRef(null)
  const loaded = access !== undefined

  // Arrived at #api-access: bring the card into view once it has its height.
  useEffect(() => {
    if (loaded && hash === `#${API_ACCESS_ANCHOR}`) scrollToElement(section.current, { block: 'center' })
  }, [loaded, hash])

  useEffect(() => {
    if (!organizationID) return undefined
    let live = true
    getOrgAccess(organizationID).then(
      a => { if (live) { setError(null); setAccess(a || null) } },
      err => { if (live) { setAccess(null); setError(serverMessage(err, "This organization's API access couldn't be loaded."), err) } }
    )
    return () => { live = false }
  }, [organizationID, setError])

  const ask = () => {
    setSaveError(null)
    setReason('')
    setNext(!access.enabled)
  }

  const save = async () => {
    if (busy) return
    const problem = reasonProblem(reason)
    if (problem) return setSaveError(problem)
    setBusy(true)
    setSaveError(null)
    try {
      const answer = await setOrgAccess(organizationID, next, reason)
      const now = answer && typeof answer === 'object' && 'enabled' in answer ? answer : { ...access, enabled: next }
      setAccess(now)
      setNext(null)
      if (onChanged) onChanged(now)
    } catch (err) {
      setSaveError(serverMessage(err, "The API access couldn't be changed."), err)
    }
    setBusy(false)
  }

  const live = access ? Number(access.live_keys) || 0 : 0

  return (
    <section aria-label="Partner API access" id={API_ACCESS_ANCHOR} ref={section}>
      <Card title="Partner API access">
        <p className="text-body-secondary small">Lets this organization's admins make API keys for its own systems. Off, every key it has is refused from its next request; nothing is revoked, and switching it back on restores them.</p>
        {error && <p className="text-danger small mb-1" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}
        {access === undefined && !error && <p className="text-body-secondary small mb-0">Loading…</p>}
        {access &&
          <>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" role="switch" id={ids.label} checked={!!access.enabled} disabled={!canUpdate || busy} onChange={ask} />
              <label className="form-check-label" htmlFor={ids.label}>Partner API access</label>
            </div>
            {!access.enabled && live > 0 && <div className="sw-notice bad mt-2" role="status"><div>{ACCESS_OFF_BANNER}</div></div>}
            <p className="small mt-2 mb-0">
              {`${live} of ${access.max_keys} live keys`}
              {access.default_rate_limit_per_minute ? ` · new keys are limited to ${access.default_rate_limit_per_minute} requests a minute` : ''}
              {' · '}<Link to={`/admin/api-keys?organization=${organizationID}`}>See its keys</Link>
            </p>
            {!canUpdate && <p className="text-body-secondary small mt-1 mb-0">Switching it needs the API key update permission.</p>}
          </>}
      </Card>

      <ConfirmDialog
        open={next !== null}
        title={next ? 'Switch on partner API access?' : 'Switch off partner API access?'}
        body={next
          ? "This organization's keys work again from their next request, and its admins can make new keys."
          : `${live} live ${live === 1 ? 'key is' : 'keys are'} refused from the next request, and no new key can be made. Nothing is revoked: switching it back on restores them.`}
        confirmText={next ? 'Switch on' : 'Switch off'}
        confirmClassName={next ? 'btn sw-btn-primary' : 'btn sw-btn-danger'}
        confirmDisabled={!reason.trim()}
        busy={busy}
        error={saveError}
        failure={saveFailure}
        onConfirm={save}
        onCancel={() => setNext(null)}>
        <div className="mb-3">
          <label className="sw-label" htmlFor={ids.reason}>Reason</label>
          <textarea id={ids.reason} className="form-control" rows={2} maxLength={600} value={reason} onChange={e => setReason(e.target.value)} />
          <p className="sw-hint small mt-1 mb-0">Kept in the audit log with the change.</p>
        </div>
      </ConfirmDialog>
    </section>
  )
}
